import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, qs, type Paged } from '../api';
import { useAdmin } from '../auth';
import { Btn, Empty, ErrorBox, Loading, Modal, Pager, fmtDate, useAction } from '../ui';

interface ReviewRow {
  id: string;
  rating: number;
  text: string | null;
  status: 'published' | 'hidden';
  createdAt: string;
  author: { id: string; firstName: string; status: string };
  seller: { id: string; firstName: string; status: string };
  listing: { id: string; title: string } | null;
  reportsCount: number;
  reports: { reason: string; createdAt: string }[];
  hiddenReason: string | null;
  hiddenBy: string | null;
  hiddenAt: string | null;
}

const FILTERS = { reported: 'С жалобами', hidden: 'Скрытые', all: 'Все' } as const;
const stars = (n: number) => '★'.repeat(n) + '☆'.repeat(5 - n);

/**
 * Отзывы о продавцах. Отзыв оставляет покупатель после переписки в чате; модератор может скрыть отзыв
 * (оскорбления, реклама, личные данные, явная неправда) или отклонить жалобы на него.
 */
export function ReviewsPage() {
  const { can } = useAdmin();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<keyof typeof FILTERS>('reported');
  const [page, setPage] = useState(1);
  const [hiding, setHiding] = useState<ReviewRow | null>(null);
  const action = useAction();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['reviews', filter, page],
    queryFn: () => api<Paged<ReviewRow>>(`/admin/reviews${qs({ filter, page, pageSize: 20 })}`),
  });
  const done = () => void qc.invalidateQueries({ queryKey: ['reviews'] });
  const post = (r: ReviewRow, path: 'restore' | 'dismiss-reports') =>
    void action.run(async () => {
      await api(`/admin/reviews/${r.id}/${path}`, { method: 'POST' });
      done();
    });

  return (
    <div>
      <h1>Отзывы</h1>
      <p className="muted">
        Отзыв о продавце может оставить только покупатель, который переписывался с ним в чате приложения. Скрытый отзыв не виден и не
        учитывается в рейтинге; автор видит пометку «скрыт модератором».
      </p>
      <div className="toolbar">
        <div className="seg" role="tablist">
          {Object.entries(FILTERS).map(([k, v]) => (
            <button key={k} type="button" role="tab" aria-selected={filter === k} className={filter === k ? 'active' : ''} onClick={() => { setFilter(k as keyof typeof FILTERS); setPage(1); }}>
              {v}
            </button>
          ))}
        </div>
      </div>
      {action.error ? <ErrorBox error={action.error} /> : null}
      {isLoading ? <Loading /> : error ? <ErrorBox error={error} onRetry={() => void refetch()} /> : !data?.items.length ? (
        <Empty text={filter === 'reported' ? 'Жалоб на отзывы нет' : 'Отзывов нет'} />
      ) : (
        <>
          <table>
            <thead><tr><th>#</th><th>Дата</th><th>Оценка и текст</th><th>Автор → продавец</th><th>Жалобы</th><th /></tr></thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.id} data-testid={`review-${r.id}`}>
                  <td>{r.id}</td>
                  <td>{fmtDate(r.createdAt)}</td>
                  <td style={{ maxWidth: 380 }}>
                    <div className="stars-ro" aria-label={`Оценка ${r.rating} из 5`}>{stars(r.rating)}</div>
                    {r.text ? <div className="pre">{r.text}</div> : <span className="muted">без текста</span>}
                    {r.listing ? <div className="small muted">Объявление #{r.listing.id} {r.listing.title}</div> : null}
                    {r.status === 'hidden' ? <div className="small warn">Скрыт{r.hiddenBy ? ` (${r.hiddenBy})` : ''}: {r.hiddenReason}</div> : null}
                  </td>
                  <td>
                    {r.author.firstName || '—'} <span className="muted">#{r.author.id}</span>
                    <div className="small muted">→ {r.seller.firstName || '—'} #{r.seller.id}</div>
                  </td>
                  <td>
                    {r.reportsCount ? <b>{r.reportsCount}</b> : '—'}
                    {r.reports.map((x, i) => <div key={i} className="small muted">«{x.reason}»</div>)}
                  </td>
                  <td>
                    {can('reports.resolve') ? (
                      <div className="row">
                        {r.status === 'published' ? <Btn kind="danger" onClick={() => setHiding(r)}>Скрыть</Btn> : <Btn kind="secondary" busy={action.busy} onClick={() => post(r, 'restore')}>Вернуть</Btn>}
                        {r.status === 'published' && r.reportsCount > 0 ? <Btn kind="secondary" busy={action.busy} onClick={() => post(r, 'dismiss-reports')}>Оставить</Btn> : null}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageSize={20} total={data.total} onPage={setPage} />
        </>
      )}
      {hiding ? <HideDialog review={hiding} onClose={() => { setHiding(null); done(); }} /> : null}
    </div>
  );
}

function HideDialog({ review, onClose }: { review: ReviewRow; onClose: () => void }) {
  const action = useAction();
  const [reason, setReason] = useState('');
  return (
    <Modal title={`Скрыть отзыв #${review.id}`} onClose={onClose}>
      <p>Отзыв перестанет показываться на странице продавца и не будет учитываться в рейтинге.</p>
      <label className="field">Причина (видна в журнале и модераторам)
        <textarea value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} placeholder="Например: оскорбления, личные данные, реклама" />
      </label>
      {action.error ? <ErrorBox error={action.error} /> : null}
      <div className="row">
        <Btn kind="danger" busy={action.busy} disabled={reason.trim().length < 3} onClick={() => void action.run(async () => { await api(`/admin/reviews/${review.id}/hide`, { method: 'POST', json: { reason } }); onClose(); })}>
          Скрыть
        </Btn>
        <Btn kind="secondary" onClick={onClose}>Отмена</Btn>
      </div>
    </Modal>
  );
}
